import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import ArrowLeftOutlinedIcon from '@mui/icons-material/ArrowLeftOutlined';
import ArrowRightOutlinedIcon from '@mui/icons-material/ArrowRightOutlined';
import { Box } from '@mui/material';
import { AreaChartProps } from '../types';
import { customXAxisLabelPlugin, customYAxisLabelPlugin } from '../chartConfigs';
import { AREA_CHART_COLORS } from './areaChartColors';
import AreaChartSkeleton from './AreaChartSkeleton';
import { buildDatasets, buildChartOptions } from './utils';
import {
  useChartTooltip,
  TooltipContainer,
  TooltipBody,
} from '../../../pages/Dashboard/components/Underwritting/Charts/components/ChartTooltip';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const DEFAULT_FILL_OPACITY = 0.2;
const DEFAULT_TENSION = 0.4;
const DEFAULT_POINT_RADIUS = 4;
const DEFAULT_HEIGHT = '400px';

const AreaChart: React.FC<AreaChartProps> = ({
  data,
  columns,
  isLoading = false,
  error = false,
  colorConfig = AREA_CHART_COLORS,
  fillOpacity = DEFAULT_FILL_OPACITY,
  stacked = false,
  tension = DEFAULT_TENSION,
  showPoints = false,
  showStroke = false,
  pointRadius = DEFAULT_POINT_RADIUS,
  yAxisFormatter = (v: number | string) => String(v),
  xAxisFormatter = (label) => label,
  tooltip,
  chartOptions = {},
  xAxisLabel,
  yAxisLabel,
  height = DEFAULT_HEIGHT,
  maxVisibleCount,
}) => {
  // ── Intersection observer (lazy render) ────────────────────────────────────
  const [isVisible, setIsVisible] = useState(false);
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerNode(node);
  }, []);

  useEffect(() => {
    if (!containerNode) return;

    const bounds = containerNode.getBoundingClientRect();
    const alreadyVisible = bounds.top >= 0 && bounds.bottom <= window.innerHeight;

    if (alreadyVisible) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      threshold: 0.1,
    });

    observer.observe(containerNode);

    return () => observer.disconnect();
  }, [containerNode]);

  // ── Viewport (React-state-managed window) ─────────────────────────────────
  // Keeping min/max in React state and passing them into buildChartOptions
  // ensures react-chartjs-2 always owns the options object — no imperative
  // chartRef manipulation that gets overwritten on the next options merge.
  const enableZoom = Boolean(maxVisibleCount && columns.length > maxVisibleCount);

  const [viewport, setViewport] = useState<{ min: number; max: number } | null>(null);

  useEffect(() => {
    if (!enableZoom) {
      setViewport(null);
      return;
    }
    setViewport({ min: 0, max: maxVisibleCount! - 1 });
  }, [enableZoom, columns.length, maxVisibleCount]);

  // ── Pan ────────────────────────────────────────────────────────────────────
  const panBy = useCallback(
    (direction: -1 | 1) => {
      if (!enableZoom) return;
      const total = columns.length;
      setViewport((prev) => {
        const min = prev?.min ?? 0;
        const max = prev?.max ?? maxVisibleCount! - 1;
        const span = max - min;
        const nextMin = Math.max(0, Math.min(total - 1 - span, min + direction));
        return { min: nextMin, max: nextMin + span };
      });
    },
    [enableZoom, columns.length, maxVisibleCount],
  );

  // ── Datasets ───────────────────────────────────────────────────────────────
  const { datasets, totalMap } = useMemo(() => {
    if (!data?.length) return { datasets: [], totalMap: {} as Record<string, number> };
    return buildDatasets(data, columns, {
      colorConfig,
      stacked,
      tension,
      showPoints,
      showStroke,
      pointRadius,
      fillOpacity,
    });
  }, [
    data,
    columns,
    colorConfig,
    stacked,
    tension,
    showPoints,
    showStroke,
    pointRadius,
    fillOpacity,
  ]);

  // ── External tooltip (React portal) ───────────────────────────────────────
  const { showTooltip } = useChartTooltip();

  const handleTooltip = useCallback(
    (context: any) => {
      if (!tooltip?.render || tooltip.enabled === false) {
        showTooltip(context, null);
        return;
      }
      const dataPoints: any[] | undefined = context.tooltip?.dataPoints;
      if (!dataPoints?.length) {
        showTooltip(context, null);
        return;
      }

      const xLabel = columns[dataPoints[0].dataIndex] ?? '';
      const total = totalMap[xLabel];

      if (total === 0) {
        showTooltip(context, null);
        return;
      }

      const nonNullPoints = dataPoints.filter((pt: any) => pt.raw != null && Number(pt.raw) !== 0);
      if (!nonNullPoints.length) {
        showTooltip(context, null);
        return;
      }

      const content = tooltip.render(nonNullPoints, xLabel, total);

      showTooltip(
        context,
        content ? (
          <TooltipContainer>
            <TooltipBody>{content}</TooltipBody>
          </TooltipContainer>
        ) : null,
      );
    },
    [tooltip, columns, showTooltip, totalMap],
  );

  // ── Chart options ──────────────────────────────────────────────────────────
  const chartConfig = useMemo(
    () =>
      buildChartOptions({
        stacked,
        yAxisFormatter,
        xAxisFormatter,
        tooltip: handleTooltip,
        chartOptions,
        yAxisLabel,
        categoryLabels: columns,
        viewportMin: viewport?.min,
        viewportMax: viewport?.max,
      }),
    [
      stacked,
      yAxisFormatter,
      xAxisFormatter,
      handleTooltip,
      chartOptions,
      yAxisLabel,
      columns,
      viewport,
    ],
  );

  // ── Axis label canvas plugins ──────────────────────────────────────────────
  const chartPlugins = useMemo(
    () => [
      ...(xAxisLabel ? [customXAxisLabelPlugin(xAxisLabel, 20)] : []),
      ...(yAxisLabel ? [customYAxisLabelPlugin(yAxisLabel)] : []),
    ],
    [xAxisLabel, yAxisLabel],
  );

  // ── Guard renders ──────────────────────────────────────────────────────────
  if (isLoading || (data !== null && data.length === 0)) {
    return <AreaChartSkeleton />;
  }

  if (error || data === null) {
    return null;
  }

  const arrowButtonSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    width: '2rem',
    height: '2rem',
    backgroundColor: '#E4E3DB61',
    border: '1px solid #2F736E14',
    borderRadius: '0.25rem',
    flexShrink: 0,
  };

  const arrowButtonIconSx = {
    color: '#2E4A4F',
    width: '2rem',
    height: '2rem',
  };

  return (
    <Box ref={containerRef} sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ height, width: '100%' }}>
        {isVisible && (
          <Line
            data={{ labels: columns, datasets }}
            options={chartConfig as any}
            plugins={chartPlugins as any}
          />
        )}
      </Box>

      {enableZoom && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pointerEvents: 'none',
            px: '2px',
          }}
        >
          <Box onClick={() => panBy(-1)} sx={{ ...arrowButtonSx, pointerEvents: 'auto' }}>
            <ArrowLeftOutlinedIcon sx={arrowButtonIconSx} />
          </Box>
          <Box onClick={() => panBy(1)} sx={{ ...arrowButtonSx, pointerEvents: 'auto' }}>
            <ArrowRightOutlinedIcon sx={arrowButtonIconSx} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AreaChart;
